import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCurriculum } from '../api/studentApi';
import './StudentHome.css';
import TopicCard from '../components/TopicCard';
import StudentHeader from '../components/StudentHeader';
import { toast } from 'react-toastify';
import StudentHero from '../components/StudentHero';
import { useUser } from '../context/UserContext';
import SubjectHeaderRow from '../components/SubjectHeaderRow';
import SubjectPillsNav from '../components/SubjectPillNav';
import StudentHeaderV2 from '../components/StudentHeaderV2';

const STUDENT_HOME_STORAGE_KEY_SUBJECT = "selected_subject";
const STUDENT_HOME_STORAGE_KEY_FILTER = "selected_filer";

const StudentHome = () => {
  const colors = [
    '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#3b82f6', '#ec4899', '#84cc16', '#22d3ee', '#f97316'
  ];

  const { user } = useUser();
  const [subjects, setSubjects] = useState([]);
  // const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🌟 Tab Navigation State tracking active subject ID
  const [activeSubjectId, setActiveSubjectId] = useState(() => {
    try {
      return parseInt(localStorage.getItem(STUDENT_HOME_STORAGE_KEY_SUBJECT));
    } catch {
      return null;
    }
  });
  const [activeFilter, setActiveFilter] = useState(() => {
    try {
      return localStorage.getItem(STUDENT_HOME_STORAGE_KEY_FILTER) || 'curriculum';
    } catch {
      return 'curriculum';
    }
  }); // 'all' | 'foundation' | 'curriculum' | 'advanced'
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem(STUDENT_HOME_STORAGE_KEY_FILTER, activeFilter);
    } catch (err) {
      console.warn('Failed to save activeFilter to localStorage:', err);
    }
  }, [activeFilter]);

  // Sync activeSubjectId changes to localStorage
  useEffect(() => {
    try {
      if (activeSubjectId !== null && activeSubjectId !== undefined) {
        localStorage.setItem(STUDENT_HOME_STORAGE_KEY_SUBJECT, JSON.stringify(activeSubjectId));
      }
    } catch (err) {
      console.warn('Failed to save activeSubjectId to localStorage:', err);
    }
  }, [activeSubjectId]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [curriculumRes] = await Promise.all([
          fetchCurriculum()
        ]);
        setSubjects(curriculumRes.data);
        // setProfile(profileRes.data);
        // console.log("Loaded profile:", profileRes.data);

        // Default to the first subject tab when data loads
        if (!activeSubjectId) {
          setActiveSubjectId(curriculumRes.data[0].id);
        }
      } catch (err) {
        console.error("Failed to sync home data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getRandomTopic = () => {
    const allTopics = subjects
      .filter(subject => subject.subjectType === 'regular')
      .flatMap(subject => subject.Topics || []);
    if (allTopics.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * allTopics.length);
    return allTopics[randomIndex];
  };

  const launchQuiz = (data) => {
    if (data) {
      handleTopicClick(data.topicId, data.topicName, data.subjectName);
    } else {
      const randomTopic = getRandomTopic();
      if (randomTopic) {
        toast.success(`Started a quiz on "${randomTopic.name}"!`);
        handleTopicClick(randomTopic.id, randomTopic.name, randomTopic.subjectName);
      } else {
        toast.error("No topics available right now.");
      }
    }
  };

  const handleTopicClick = (topicId, topicName, subjectName) => {
    navigate(`/quiz/${topicId}`, { state: { title: topicName, subject: subjectName } });
  };


  // Find index and details of the currently selected subject tab
  const activeSubjectIndex = subjects.findIndex(s => s.id === activeSubjectId);
  const activeSubject = subjects[activeSubjectIndex];
  const activeColor = colors[activeSubjectIndex % colors.length] || colors[0];

  const completedCount = activeSubject?.Topics?.reduce((count, topic) =>
    topic.currentLevel > 5 ? count + 1 : count, 0
  ) || 0;

  const handlePlaySideQuestClick = () => {
    const t = subjects.find((sub) => sub.subjectType === 'quests');
    setActiveSubjectId(t.id);
  };

  return (
    <div className="student-home">
      <StudentHeaderV2/>
      {loading ? (<div className="home-loading-screen">
        <div className="loader"></div>
        <p>Opening your classroom...</p>
      </div>) : (<>
        <StudentHero onStartQuiz={launchQuiz} profile={user} subjects={subjects} onStartQuest={handlePlaySideQuestClick} />

        {/* 🌟 2. Subject Filter Navigation Pills */}
        <SubjectPillsNav 
        subjects={subjects} 
        activeSubjectId={activeSubjectId}
        setActiveSubjectId={(id) => setActiveSubjectId(id)}
        colors={colors}/>

        {/* 🌟 3. Isolated Target Subject Viewport */}
        {activeSubject && (() => {
          // Determine if this subject is Side Quests and whether it's locked
          const isSideQuestDisabled = activeSubject.subjectType === 'quests' && user.quests.questsLocked;
          const filteredTopics = (activeSubject?.Topics || []).filter((topic) => {
            if (activeFilter === 'all') return true;
            const topicType = (topic.topicType || 'curriculum').toLowerCase();
            return topicType === activeFilter;
          });

          return (
            <main className={`learning-path-focused ${isSideQuestDisabled ? 'quests-disabled-view' : ''}`}>
              <SubjectHeaderRow
                activeSubject={activeSubject}
                isSideQuestDisabled={isSideQuestDisabled}
                activeColor={activeColor}
                completedCount={completedCount}
                activeFilter={activeFilter}
                onFilterChange={(newFilter) => setActiveFilter(newFilter)}
              />

              {/* Symmetrical Grid for the Active Tab's Topics Only */}
              <div className="topics-grid-focused">
                {filteredTopics.length > 0 ? (
                  filteredTopics.map((topic) => (
                    <TopicCard
                      key={topic.id}
                      topic={topic}
                      color={activeColor}
                      topicType={topic.topicType}
                      disabled={isSideQuestDisabled}
                      onClick={() => {
                        if (isSideQuestDisabled) return; // Prevent click navigation
                        handleTopicClick(topic.id, topic.name, topic.subjectName);
                      }}
                    />
                  ))
                ) : (
                  <div className="empty-topic-state">
                    {activeSubject?.Topics && activeSubject.Topics.length > 0 ? (
                      <p>No <strong>{activeFilter}</strong> topics found for {activeSubject.name}.</p>
                    ) : (
                      <p>New topics for {activeSubject?.name} are currently being drafted!</p>
                    )}
                  </div>
                )}
              </div>
            </main>
          );
        })()}
      </>)}

    </div>
  );
};

export default StudentHome;